import BaseView from '../../../views/base-view';
import DragDistanceHelper from '../../../tools/drag-distance-helper';
import DrawerView from '../../../views/drawer-view';
import ParametricPartView from '../../../views/parametric-part-view';
import Signal from '../../../control-flow/signal';
import Tool from '../../../tools/tool';
import UnitEditorApp from '../unit-editor-app';
import logging from '../../../logging';
import visibleSize from '../../../view3d/visible-size';
import { Box3, Object3D, Vector3 } from 'three';
import { Door, Drawer } from '../../../domain';
import { getParentSectionView, isInsideSection } from '../../../controllers/section-controllers';
import { getParentView } from '../../../controllers/base-view-controllers';
import { isBoxOnly } from '../../../controllers/drawer-controllers';
import { overlapsSectionContent, overlapsSectionDoor } from '../../../view3d/overlaps-section-view';
import { resizeCubbyByFixedShelf } from '../../../controllers/cubby-controllers';
import { resizeDoorByFixedShelf } from '../../../controllers/door-controllers';
import { approx, approxOverlaps, getAvailableSlots, round2slot, slotAbove, slotBelow, slotFloor } from '../../../measurement';
import SectionView from '../../../views/section-view';
import { always, firstof, isInstanceOf, match, prop } from '../../../control-flow/action-on-match';
import GenericAssetView from '../../../views/generic-asset-view';
import SlantedShelfView from '../../../views/slanted-shelf-view';
import { getSlantedShelfHeight } from '../../../controllers/slanted-shelf-controllers';
import DoorView from '../../../views/door-view';
import CubbyView from '../../../views/cubby-view';

const log = logging('tools');

export class MoveTool extends Tool {

    app: UnitEditorApp;
    helper: DragDistanceHelper;
    view?: BaseView;

    /**
     * If view is defined, then grabOffset contains the offset between where the mouse
     * was pressed and the origin of the view object.
     */
    grabOffset: number;

    /**
     * The initial position of the object
     */
    startY: number;

    isValidMovement: (y: number) => boolean;

    // Scratch instances
//    box = new Box3();
//    v = new Vector3();

    onMove = new Signal('onMove', 'tools');

    constructor(app: UnitEditorApp, helper: DragDistanceHelper) {
        super();
        this.app = app;
        this.helper = helper;
    }

    enable(): void {
        log.debug('MoveTool.enable()');
        this.helper.on('dragstart', this.begin);
    }

    disable(): void {
        log.debug('MoveTool.disable()');
        this.helper.off('dragstart', this.begin);
    }

    begin(): void {
        log.debug('MoveTool.begin()');
        // Note that we get the hit from the DragDistanceHelper here instead of
        // the picker. The helper retains the hit object at the position where
        // the pointer was pressed down, which improves the feeling of hit
        // accuracy that the drag distance would otherwise degrade.
        const top = this.helper.hit;

        if(top && top.userData.isMovable) {
            this.view = getParentView(top) as BaseView;

            this.startY = round2slot(this.view.model.position.y);
            this.grabOffset = this.startY - this.helper.current.y;

            this.isValidMovement = constructValidator(this.view);

            this.helper.on('drag', this.move);
            this.helper.on('dragend', this.end);
        }
    }

    move = () => {
        //log.debug('MoveTool.move()');
        const
            y = round2slot(this.helper.current.y + this.grabOffset),
            dy = y - this.startY;
            //sectionView = getParentSectionView(this.view),
            //box = this.box,
            //v = this.v;

//        this.box.makeEmpty();
//        visibleSize(this.view, box);
//        this.view.getWorldPosition(v);
//
//        const
//            height = box.max.y - box.min.y,
//            newBottom = y + (box.min.y - v.y); // adjust for object origin
//
//        if(!isInsideSection(sectionView.model, y))
//            return;
//        if(overlapsSectionContent(sectionView, newBottom, height, this.view))
//            return;
//        if(this.view instanceof DrawerView) {
//            if(!isBoxOnly(this.view.model as Drawer)
//               && overlapsSectionDoor(sectionView, newBottom, height, this.view)) {
//                return;
//            }
//        }
//        else if(this.view instanceof ParametricPartView) {
//            if(this.view.model.name.startsWith('Fixed')) {
//                resizeDoorByFixedShelf(this.view.model, sectionView.model, y);
//                resizeCubbyByFixedShelf(this.view.model, sectionView.model, y);
//            }
//        }

        if(this.isValidMovement(dy)) {
            if(isFixedShelf(this.view)) {
                // Doors
                {
                    const [above, below] = getAdjacentDoors(this.view);
                    if(above) {
                        const
                            oldTop = round2slot(
                                above.model.position.y + above.model.height),
                            newBottom = y;
                        above.model.position.y = newBottom;
                        above.model.height = oldTop - newBottom;
                    }
                    if(below) {
                        const
                            oldBottom = round2slot(below.model.position.y),
                            newTop = y; // round2slot(oldBottom + below.model.height + dy);
                        below.model.height = newTop - oldBottom;
                    }
                }
                // Cubbies
                {
                    const [above, below] = getAdjacentCubbies(this.view);
                    if(above) {
                        const
                            oldTop = round2slot(
                                above.model.position.y + above.model.height),
                            newBottom = y + 3/8;
                        above.model.position.y = newBottom;
                        above.model.height = oldTop - y - this.view.model.height;
                    }
                    if(below) {
                        const
                            oldBottom = below.model.position.y,
                            newTop = y;
                        below.model.height =
                            newTop - oldBottom + 3/8 - this.view.model.height;
                    }
                }
            }
            this.view.model.position.y = y;
            this.onMove.trigger();
        }
    }

    cancel(): void {
        log.debug('MoveTool.cancel()');
        if(this.view) {
            this.onMove.trigger();
            delete this.view;
        }

        delete this.isValidMovement
        this.helper.off('drag', this.move);
        this.helper.off('dragend', this.end);
    }

    end(): void {
        log.debug('MoveTool.end()');
        this.app.commit('move', 'move');
        delete this.view;
        delete this.isValidMovement
        this.helper.off('drag', this.move);
        this.helper.off('dragend', this.end);
    }
}
export default MoveTool;


/**
 * Tests if this view corresponds to a fixed shelf
 */
const isFixedShelf = (obj: Object3D): obj is ParametricPartView =>
    (obj instanceof ParametricPartView) && obj.model.name?.includes('Fixed')


type AdjacentDoors = [above: DoorView|null, below: DoorView|null];


/**
 * Returns a two-tuple of DoorViews if a door exists directly above or below
 * the given shelf.
 */
export const getAdjacentDoors = (shelf: ParametricPartView): AdjacentDoors =>
{
    const
        y = round2slot(shelf.model.position.y),
        section = getParentSectionView(shelf),
        result: AdjacentDoors = [null, null];
    for(const child of section.children) {
        if(child instanceof DoorView) {
            const
                bottom = round2slot(child.model.position.y),
                top = round2slot(bottom + child.model.height);
            if(y == bottom)
            {
                result[0] = child;
            } else if(y == top) {
                result[1] = child;
            }
        }
    }
    return result;
}


type AdjacentCubbies = [above: CubbyView|null, below: CubbyView|null];


/**
 * Returns a two-tuple of CubbyViews if a cubby exists directly above or below
 * the given shelf.
 */
export const getAdjacentCubbies = (shelf: ParametricPartView): AdjacentCubbies =>
{
    const
        y = round2slot(shelf.model.position.y),
        section = getParentSectionView(shelf),
        result: AdjacentCubbies = [null, null];

    for(const child of section.children) {
        if(child instanceof CubbyView) {
            const
                bottom = child.model.position.y - 3/8,
                top = bottom + child.model.height + shelf.model.height;
            if(approx(bottom, y)) {
                result[0] = child;
            }
            else if(approx(top, y)) {
                result[1] = child;
            }
        }
    }

    return result;
}


type Interval = [start: number, end: number];
type Obstacle = [front: Interval | null, interior: Interval | null];


/**
 * Creates a function that checks whether the proposed move would violate the
 * layout constraints on the section. The move tool creates a new validator
 * function every time
 */
const constructValidator = (movable: BaseView) => {
    const
        // The obstacles include everything except the movable
        obstacles = createObstacles(movable),
        // The "movable obstacle" (mo) is the space that the movable obstructs
        mo = convertToObstacle(movable);

    // If the movable is a fixed shelf and it controls a door, then add an
    // obstacle that limits the movement so that the door is no less than five
    // inches tall.
    if(isFixedShelf(movable)) {
        // Doors
        {
            const [above, below] = getAdjacentDoors(movable);
            if(above) {
                const
                    dy = above.model.height - 5,
                    y = round2slot(above.model.position.y) + dy;
                obstacles.push([null, [y, Infinity]]);
            }
            if(below) {
                const
                    y = round2slot(below.model.position.y) + 5;
                obstacles.push([null, [-Infinity, y]]);
            }
        }
        // Cubbies
        {
            const [above, below] = getAdjacentCubbies(movable);
            if(above) {
                const
                    dy = above.model.height
                        - (above.model.countY * above.model.thickness),
                    y = round2slot(above.model.position.y) + dy;
                obstacles.push([null, [y, Infinity]]);
            }
            if(below) {
                const
                    y = round2slot(below.model.position.y)
                        + 3/8
                        + (below.model.countY * below.model.thickness);
                obstacles.push([null, [-Infinity, y]]);
            }
        }
    }

    return (dy: number) => {
        const
            addDy = (y: number) => y + dy,
            front = mo ? mo[0]?.map(addDy) as Interval : null,
            interior = mo ? mo[1]?.map(addDy) as Interval : null;
        for(const [ofront, ointerior] of obstacles) {
            if(front != null && ofront != null
               && approxOverlaps(front[0], front[1], ofront[0], ofront[1]))
            {
                return false;
            }
            if(interior != null && ointerior != null
               && approxOverlaps(interior[0], interior[1], ointerior[0], ointerior[1]))
            {
                return false;
            }
        }

        return true;
    }
}

const createObstacles = (movable: BaseView) => {
    const
        section = getParentSectionView(movable),
        obstacles: Obstacle[] = [];

    section.traverse(obj => {
        if(obj === movable) return;
        const obstacle = convertToObstacle(obj);
        if(obstacle) obstacles.push(obstacle);
    });

    return obstacles;
};

/**
 * Predicate that tests if the name property matches the given regular
 * expression.
 */
const partName = (pattern: RegExp) => prop('name', pattern);

/**
 * Finds the top and bottom of an arbitrary object.
 */
const measureInterval = (obj: Object3D): Interval => {
    const box = new Box3();
    visibleSize(obj, box);
    return [box.min.y, box.max.y];
};

const convertToObstacle = (obj: Object3D): Obstacle | null => {
    let obstacle: Obstacle = null;

    firstof(
        match(isInstanceOf(DoorView), door => {
            const
                bottom = round2slot(door.model.position.y),
                top = bottom + door.model.height;
            obstacle = [[bottom, top], null];
        }),
        match(isInstanceOf(DrawerView), firstof(
            match(prop('panel', null), (drawer: DrawerView) => {
                obstacle = [null, measureInterval(drawer)];
            }),
            always((drawer: DrawerView) => {
                const
                    bottom = round2slot(drawer.model.position.y),
                    top = bottom + drawer.model.height;
                obstacle = [[bottom, top], [bottom + 0.5, top - 0.5]];
            }),
        )),
        match(isInstanceOf(GenericAssetView), asset => {
            obstacle = [null, measureInterval(asset)];
        }),
        match(isInstanceOf(ParametricPartView), firstof(
            match(partName(/PuckLight/), (part: ParametricPartView) => {
                const
                    origin = round2slot(part.model.position.y),
                    top = origin + 3/8,
                    bottom = top - part.model.height - 1;
                obstacle = [null, [bottom, top]];
            }),
            match(partName(/^Fixed/), (part: ParametricPartView) => {
                const
                    origin = round2slot(part.model.position.y),
                    top = origin + 3/8,
                    bottom = top - part.model.height;
                obstacle = [null, [bottom, top]];
            }),
            match(partName(/Shelf|ToeKick/), (part: ParametricPartView) => {
                const
                    bottom = round2slot(part.model.position.y),
                    top = bottom + part.model.height;
                obstacle = [null, [bottom, top]];
            }),
            // Anything we don't know how to measure from a domain model, we
            // can measure the actually realized view. The other cases are
            // basically an optimization on this one.
            always((part: ParametricPartView) => {
                const
                    box = new Box3(),
                    v = new Vector3();
                visibleSize(part, box);
                part.getWorldPosition(v);
                obstacle = [null, measureInterval(part)];
            }),
        )),
        match(isInstanceOf(SlantedShelfView), shelf => {
            const
                bottom = round2slot(shelf.model.position.y),
                top = getSlantedShelfHeight(shelf.model);
            obstacle = [null, [bottom, top]];
        }),
    )(obj);

    return obstacle;
}

